// Ruta: @app/services/notificaciones/notificaciones-header.service.ts
import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { environment } from '@src/environments/environment';
import { Notificacion, TipoNotificacion } from '@app/models/notificacion.model';
import { PaginatedResponse } from '@app/models/backend/notification/paginated-response.model';

// --- Interfaces para tipado del backend (Header: liviano) ---
interface BackendNotification {
  id: number;
  recipientId: number | null;
  title: string;
  message: string;
  type: string | null;
  category?: string | null;
  read: boolean;
  createdAt: string; // puede venir "DD/MM/YYYY HH:mm:ss" o ISO
  senderName?: string | null;
  senderId?: number | null;
  avatar?: string | null;
  // (sin readers aquí)
}

interface ApiResponse<T> {
  rpta?: number;
  msg?: string;
  data: T;
}

@Injectable({ providedIn: 'root' })
export class NotificacionesHeaderService {
  private baseUrl = `${environment.url}api/notifications`;

  private refreshCountSubject = new Subject<void>();
  public refreshCount$ = this.refreshCountSubject.asObservable();

  private contadorNoLeidasSubject = new BehaviorSubject<number>(0);
  public contadorNoLeidas$ = this.contadorNoLeidasSubject.asObservable();

  constructor(private http: HttpClient) {}

  // ============================================================
  // Contador (GET /unread-count/{userId})
  // ============================================================
  obtenerContadorNoLeidas(): Observable<number> {
    const userId = this.getUserId();
    if (!userId) return of(0);

    const url = `${this.baseUrl}/unread-received-count/${userId}`;
    return this.http.get<ApiResponse<number> | { data: number }>(url).pipe(
      map((resp: any) => {
        const count = typeof resp?.data === 'number' ? resp.data : 0;
        this.contadorNoLeidasSubject.next(count);
        return count;
      }),
      catchError((err) => {
        console.error('❌ Error al obtener contador (header):', err);
        this.contadorNoLeidasSubject.next(0);
        return of(0);
      })
    );
  }

  public triggerRefreshCount(): void {
    this.refreshCountSubject.next();
  }

  // ============================================================
  // Header: No leídas / Leídas (GET /header/unread|read/{userId})
  // ============================================================
  obtenerNoLeidasHeader(
    page: number,
    size: number
  ): Observable<PaginatedResponse<Notificacion>> {
    const userId = this.getUserId();
    if (!userId) return of(this.getEmptyPaginatedResponse());

    const url = `${this.baseUrl}/header/unread-received/${userId}`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    return this.http
      .get<
        | ApiResponse<PaginatedResponse<BackendNotification>>
        | PaginatedResponse<BackendNotification>
      >(url, { params })
      .pipe(
        map((resp) => this.normalizeAndMapPage(resp)),
        catchError((error) => {
          console.error('❌ Error al obtener NO LEÍDAS (header):', error);
          return of(this.getEmptyPaginatedResponse());
        })
      );
  }

  obtenerLeidasHeader(
    page: number,
    size: number
  ): Observable<PaginatedResponse<Notificacion>> {
    const userId = this.getUserId();
    if (!userId) return of(this.getEmptyPaginatedResponse());

    const url = `${this.baseUrl}/header/read-received/${userId}`;
    const params = new HttpParams()
      .set('page', String(page))
      .set('size', String(size));

    return this.http
      .get<
        | ApiResponse<PaginatedResponse<BackendNotification>>
        | PaginatedResponse<BackendNotification>
      >(url, { params })
      .pipe(
        map((resp) => this.normalizeAndMapPage(resp)),
        catchError((error) => {
          console.error('❌ Error al obtener LEÍDAS (header):', error);
          return of(this.getEmptyPaginatedResponse());
        })
      );
  }

  // ============================================================
  // Acciones
  // ============================================================
  marcarComoLeida(notificacionId: number): Observable<boolean> {
    const userId = this.getUserId();
    if (!userId) return of(false);

    const url = `${this.baseUrl}/mark-as-read/${notificacionId}/${userId}`;
    return this.http
      .put<{ data: boolean } | ApiResponse<boolean>>(url, null)
      .pipe(
        map((res: any) => res?.data === true),
        catchError((err) => {
          console.error('❌ Error al marcar notificación como leída:', err);
          return of(false);
        })
      );
  }

  marcarTodasComoLeidas(): Observable<boolean> {
    const userId = this.getUserId();
    if (!userId) return of(false);

    const url = `${this.baseUrl}/mark-all-as-read/${userId}`;
    return this.http
      .put<{ data: boolean } | ApiResponse<boolean>>(url, null)
      .pipe(
        map((res: any) => res?.data === true),
        tap((ok) => ok && this.obtenerContadorNoLeidas().subscribe()),
        catchError((err) => {
          console.error('❌ Error al marcar todas como leídas:', err);
          return of(false);
        })
      );
  }

  // ============================================================
  // Helpers
  // ============================================================
  private normalizeAndMapPage(
    resp:
      | ApiResponse<PaginatedResponse<BackendNotification>>
      | PaginatedResponse<BackendNotification>
  ): PaginatedResponse<Notificacion> {
    const backendPage = (resp as any)?.data ?? resp;
    const contentRaw = Array.isArray(backendPage?.content)
      ? (backendPage.content as BackendNotification[])
      : [];
    const content = contentRaw.map((n) => this.mapBackendToFrontend(n));

    const totalPages = Number(backendPage?.totalPages ?? 0);
    const number = Number(backendPage?.number ?? 0);
    const size = Number(backendPage?.size ?? content.length ?? 0);
    const numberOfElements = Number(
      backendPage?.numberOfElements ?? content.length ?? 0
    );
    const totalElements = Number(
      backendPage?.totalElements ?? numberOfElements
    );
    const last =
      typeof backendPage?.last === 'boolean'
        ? backendPage.last
        : number + 1 >= totalPages;
    const first =
      typeof backendPage?.first === 'boolean'
        ? backendPage.first
        : number === 0;
    const empty = content.length === 0;

    return {
      content,
      totalPages,
      totalElements,
      last,
      first,
      size,
      number,
      numberOfElements,
      empty,
    };
  }

  private getEmptyPaginatedResponse(): PaginatedResponse<Notificacion> {
    return {
      content: [],
      totalPages: 0,
      totalElements: 0,
      last: true,
      first: true,
      size: 0,
      number: 0,
      numberOfElements: 0,
      empty: true,
    };
  }

  /** Convierte "DD/MM/YYYY HH:mm:ss" a ISO. Si ya es ISO o parseable, la devuelve en ISO. */
  private parseCustomDateString(dateStr: string | null | undefined): string {
    if (!dateStr) return new Date(NaN).toISOString();

    // Si parece ISO (contiene '-' o 'T'), no tocar
    if (dateStr.includes('-') || dateStr.includes('T')) {
      const d = new Date(dateStr);
      return isNaN(+d) ? new Date(NaN).toISOString() : d.toISOString();
    }

    // Formato DD/MM/YYYY HH:mm:ss
    if (dateStr.includes('/')) {
      const [fecha, hora = '00:00:00'] = dateStr.split(' ');
      const [dS, mS, yS] = fecha.split('/');
      const [hS, minS, sS] = hora.split(':');

      const d = parseInt(dS, 10);
      const m = parseInt(mS, 10) - 1;
      const y = parseInt(yS, 10);
      const hh = parseInt(hS || '0', 10);
      const mm = parseInt(minS || '0', 10);
      const ss = parseInt(sS || '0', 10);

      const dt = new Date(y, m, d, hh, mm, ss);
      return isNaN(+dt) ? new Date(NaN).toISOString() : dt.toISOString();
    }

    const dt = new Date(dateStr);
    return isNaN(+dt) ? new Date(NaN).toISOString() : dt.toISOString();
  }

  private mapBackendToFrontend(n: BackendNotification): Notificacion {
    return {
      id: n.id,
      usuarioId: n.recipientId ?? 0,
      titulo: n.title || 'Sin título',
      mensaje: n.message || 'Sin mensaje',
      tipo: this.mapTipoNotificacion((n.type || '').toUpperCase()),
      leida: !!n.read,
      fechaCreacion: this.parseCustomDateString(n.createdAt),
      datos: undefined,
      senderName: n.senderName ?? undefined,
      senderId: n.senderId ?? undefined,
      recipientId: n.recipientId ?? undefined,
    };
  }

  private mapTipoNotificacion(backendType: string): TipoNotificacion {
    const typeMap: Record<string, TipoNotificacion> = {
      INFO: TipoNotificacion.INFO,
      USER_MESSAGE: TipoNotificacion.INFO,
      SUCCESS: TipoNotificacion.EXITO,
      ALERT: TipoNotificacion.ADVERTENCIA,
      WARNING: TipoNotificacion.ADVERTENCIA,
      ERROR: TipoNotificacion.ERROR,
      SYSTEM: TipoNotificacion.SISTEMA,
      BROADCAST: TipoNotificacion.SISTEMA,
    };
    return typeMap[backendType] ?? TipoNotificacion.INFO;
  }

  private getUserId(): number | null {
    try {
      const raw =
        localStorage.getItem('user') || localStorage.getItem('crm_user');
      const u = raw ? JSON.parse(raw) : null;
      return u?.id ? Number(u.id) : null;
    } catch (e) {
      console.error('🔔 Error al obtener userId:', e);
      return null;
    }
  }
}
